import React from 'react';
import { Shield, CheckCircle, ArrowRight, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PRODUCTS } from '../lib/stripe-config';
import { useStripe } from '../hooks/useStripe';
import { Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';

export default function Pricing() {
  const { user } = useAuth();
  const { loading, createCheckoutSession } = useStripe();
  const { subscription } = useSubscription();

  const handleSubscribe = async (productId: keyof typeof PRODUCTS) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    await createCheckoutSession(productId);
  };

  const isLifetimePlan = subscription?.tier === 'LIFETIME';

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Shield className="h-12 w-12 text-orange-600 mx-auto" />
          <h1 className="mt-4 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            Simple, transparent pricing
          </h1>
          <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
            Start free, upgrade when you're ready
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12 max-w-4xl mx-auto">
          {/* Free Tier */}
          <div className="bg-white rounded-2xl shadow-xl border p-8 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl relative border-gray-100">
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900">Free</h3>
              <p className="mt-4 text-sm text-gray-500">Perfect for trying out WAFManager Pro</p>
              <p className="mt-8 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">Free</span>
              </p>

              <ul className="mt-8 space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="ml-3 text-base text-gray-700">Up to 10 domains</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="ml-3 text-base text-gray-700">1 Cloudflare account</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="ml-3 text-base text-gray-700">Core WAF templates</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="ml-3 text-base text-gray-700">Basic DNS management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <span className="ml-3 text-base text-gray-700">Community support</span>
                </li>
              </ul>
            </div>

            <div className="mt-8">
              {user ? (
                isLifetimePlan ? (
                  <button
                    disabled
                    className="w-full inline-flex items-center justify-center px-5 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-500 bg-gray-50 cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full inline-flex items-center justify-center px-5 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-500 bg-gray-50 cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                )
              ) : (
                <Link
                  to="/signup"
                  className="w-full inline-flex items-center justify-center px-5 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Sign Up
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
            </div>
          </div>

          {/* Lifetime Tier */}
          <div className="bg-white rounded-2xl shadow-xl border p-8 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl relative border-orange-200">
            <div className="absolute top-0 right-8 -translate-y-1/2">
              <div className="inline-flex items-center px-4 py-1 rounded-full text-sm font-semibold bg-orange-100 text-orange-800">
                <Zap className="h-4 w-4 mr-1" />
                Best Value
              </div>
            </div>

            <div className="flex-1">
              <h3 className="text-2xl font-bold text-gray-900">{PRODUCTS.LIFETIME.name}</h3>
              <p className="mt-4 text-sm text-gray-500">{PRODUCTS.LIFETIME.description}</p>
              <p className="mt-8 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900">$99</span>
                <span className="ml-1 text-xl font-medium text-gray-500">AUD</span>
                <span className="ml-2 text-base font-medium text-gray-500">one-time</span>
              </p>

              <ul className="mt-8 space-y-4">
                {PRODUCTS.LIFETIME.features.map((feature) => (
                  <li key={feature} className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                    <span className="ml-3 text-base text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8">
              {user ? (
                isLifetimePlan ? (
                  <button
                    disabled
                    className="w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-400 cursor-not-allowed"
                  >
                    Current Plan
                  </button>
                ) : (
                  <button
                    onClick={() => handleSubscribe('LIFETIME')}
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        Buy Now
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </button>
                )
              ) : (
                <Link
                  to="/login"
                  className="w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  Sign in to Subscribe
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-base text-gray-500">
            Questions?{' '}
            <a href="mailto:customercare@shadowtek.com.au" className="font-medium text-orange-600 hover:text-orange-500">
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}